// P1 product sheets (frozen mockups-cat-v1): SCR-CAT-02 product, SCR-CAT-04 price change, SCR-CAT-05 barcodes,
// SCR-CAT-06 category and brand, SCR-CAT-10 CSV export. The server decides; the checks here only give instant feedback.
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BottomSheet,
  Button,
  Chips,
  Field,
  FormGrid,
  Input,
  Notice,
  Scanner,
  Select,
  SheetActions,
  Tabs,
} from '../../design/kit';
import {
  addBarcode,
  changePrice,
  deleteProduct,
  findBarcode,
  generateBarcode,
  saveBrand,
  saveCategory,
  saveProduct,
  toggleProduct,
  type Brand,
  type Category,
  type Product,
  type ProductCost,
  type ProductPrice,
  type Unit,
} from '../../data/catalog';
import { Code, Name } from '../../i18n/Isolate';
import { CameraScan } from './CameraScan';
import { CATALOG, parseAmount, parseWhole, refusalCode, useMoney, useRefusalText } from './common';

type Errors = Partial<Record<'name' | 'sku' | 'capacity' | 'price' | 'units' | 'reorder' | 'form', string>>;

/** SCR-CAT-02: create or edit a product (owner). Prices are set at creation; later through the price sheet. */
export function ProductSheet({
  product,
  price,
  categories,
  brands,
  families = [],
  onFamily,
  onClose,
  onSaved,
}: {
  product: Product | null;
  price: ProductPrice | undefined;
  categories: Category[];
  brands: Brand[];
  /** PRD-07: a new product can be a variant of a family; choosing one opens the variant sheet. */
  families?: { id: string; name: string }[];
  onFamily?: (family: string) => void;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refusal = useRefusalText();
  const editing = product !== null;
  const locked = product?.has_history ?? false;
  const [name, setName] = useState(product?.name ?? '');
  const [sku, setSku] = useState(product?.sku ?? '');
  const [category, setCategory] = useState(product?.category_id ?? '');
  const [brand, setBrand] = useState(product?.brand_id ?? '');
  const [units, setUnits] = useState<Unit[]>(
    product
      ? [...(product.sell_piece ? ['piece' as const] : []), ...(product.sell_box ? ['box' as const] : [])]
      : ['piece'],
  );
  const [capacity, setCapacity] = useState(product?.box_capacity?.toString() ?? '');
  const [piecePrice, setPiecePrice] = useState('');
  const [boxPrice, setBoxPrice] = useState('');
  const [reorder, setReorder] = useState(product ? String(product.reorder_level) : '0');
  const [notes, setNotes] = useState(product?.notes ?? '');
  const [errors, setErrors] = useState<Errors>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const money = useMoney();
  const done = () => void qc.invalidateQueries({ queryKey: CATALOG });

  const save = useMutation({
    mutationFn: () =>
      saveProduct({
        id: product?.id ?? null,
        name: name.trim(),
        sku: sku.trim(),
        category: category || null,
        brand: brand || null,
        boxCapacity: capacity.trim() ? Number(capacity) : null,
        sellPiece: units.includes('piece'),
        sellBox: units.includes('box'),
        piecePrice: parseAmount(piecePrice),
        boxPrice: parseAmount(boxPrice),
        reorderLevel: Number(reorder || 0),
        notes,
      }),
    onSuccess: (r) => {
      done();
      onSaved(r.id);
    },
    onError: (e) => {
      const code = refusalCode(e);
      const text = refusal(e, 'err.prodname');
      const field: keyof Errors =
        code === 'NAME_TAKEN' || code === 'NAME_REQUIRED'
          ? 'name'
          : code === 'SKU_TAKEN'
            ? 'sku'
            : code === 'INVALID_CAPACITY' || code === 'PRODUCT_LOCKED'
              ? 'capacity'
              : code === 'PRICE_REQUIRED'
                ? 'price'
                : code === 'UNIT_REQUIRED'
                  ? 'units'
                  : 'form';
      setErrors({ [field]: text, ...(field !== 'form' ? { form: t('err.formhead') } : {}) });
    },
  });
  const toggle = useMutation({
    mutationFn: () => toggleProduct(product?.id ?? '', !product?.active),
    onSuccess: () => {
      done();
      onClose();
    },
    onError: (e) => setErrors({ form: refusal(e) }),
  });
  const remove = useMutation({
    mutationFn: () => deleteProduct(product?.id ?? ''),
    onSuccess: () => {
      done();
      onSaved('');
    },
    onError: (e) => setErrors({ form: refusal(e) }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: Errors = {};
    if (!name.trim()) next.name = t('err.required');
    if (units.length === 0) next.units = t('err.unit');
    const cap = parseWhole(capacity);
    if (units.includes('box') && (cap === null || Number.isNaN(cap) || cap < 2)) next.capacity = t('err.capacity');
    else if (cap !== null && (Number.isNaN(cap) || cap < 2)) next.capacity = t('err.capacity');
    const needPiece = units.includes('piece') && !(editing && price?.piece_price);
    const needBox = units.includes('box') && !(editing && price?.box_price);
    if ((needPiece && !(parseAmount(piecePrice) ?? 0)) || (needBox && !(parseAmount(boxPrice) ?? 0)))
      next.price = t('err.price');
    const r = parseWhole(reorder || '0');
    if (r === null || Number.isNaN(r) || r < 0) next.reorder = t('err.number');
    if (Object.keys(next).length) {
      setErrors({ ...next, form: t('err.formhead') });
      return;
    }
    setErrors({});
    save.mutate();
  };

  const toggleUnit = (id: string) => {
    if (locked) return;
    const u = id as Unit;
    setUnits((cur) => (cur.includes(u) ? cur.filter((x) => x !== u) : [...cur, u]));
  };
  const busy = save.isPending || toggle.isPending || remove.isPending;
  const showPieceInput = units.includes('piece') && !(editing && price?.piece_price);
  const showBoxInput = units.includes('box') && !(editing && price?.box_price);

  return (
    <BottomSheet title={t(editing ? 'editproduct' : 'newproduct')} open onClose={onClose} busy={busy}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {errors.form && <Notice tone="danger">{errors.form}</Notice>}
        {!editing && onFamily && families.length > 0 && (
          <Field label={t('f.family')}>
            {({ id }) => (
              <Select id={id} value="" onChange={(e) => e.target.value && onFamily(e.target.value)}>
                <option value="">{t('none')}</option>
                {families.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label={t('name')} error={errors.name}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="auto"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <Field label={t('sku')} hint={t('sku.hint')} error={errors.sku}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="ltr"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <FormGrid>
          <Field label={t('category')}>
            {({ id }) => (
              <Select id={id} value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">{t('none')}</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.path}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('brand')}>
            {({ id }) => (
              <Select id={id} value={brand} onChange={(e) => setBrand(e.target.value)}>
                <option value="">{t('none')}</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </FormGrid>
        <Field label={t('units')} hint={locked ? t('lock.hint') : undefined} error={errors.units}>
          {() => (
            <Chips
              label={t('units')}
              items={[
                { id: 'piece', label: t('unit.piece') },
                { id: 'box', label: t('unit.box') },
              ]}
              selected={units}
              onToggle={toggleUnit}
              fixed={locked ? ['piece', 'box'] : []}
            />
          )}
        </Field>
        <Field label={t('capacity')} hint={locked ? undefined : t('capacity.hint')} error={errors.capacity}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              numeric
              inputMode="numeric"
              value={capacity}
              readOnly={locked}
              onChange={(e) => setCapacity(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        {(showPieceInput || showBoxInput) && (
          <FormGrid>
            {showPieceInput && (
              <Field label={t('pieceprice')} error={errors.price}>
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    numeric
                    inputMode="decimal"
                    value={piecePrice}
                    onChange={(e) => setPiecePrice(e.target.value)}
                    aria-describedby={describedBy}
                    aria-invalid={invalid}
                  />
                )}
              </Field>
            )}
            {showBoxInput && (
              <Field label={t('boxprice')} error={showPieceInput ? undefined : errors.price}>
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    numeric
                    inputMode="decimal"
                    value={boxPrice}
                    onChange={(e) => setBoxPrice(e.target.value)}
                    aria-describedby={describedBy}
                    aria-invalid={invalid}
                  />
                )}
              </Field>
            )}
          </FormGrid>
        )}
        {editing && (price?.piece_price || price?.box_price) && (
          <Field label={`${t('pieceprice')} · ${t('boxprice')}`} hint={t('price.viacard')}>
            {({ id }) => (
              <Input
                id={id}
                readOnly
                value={[money(price?.piece_price), money(price?.box_price)].filter(Boolean).join(' · ')}
              />
            )}
          </Field>
        )}
        <Field label={t('reorder')} error={errors.reorder}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              numeric
              inputMode="numeric"
              value={reorder}
              onChange={(e) => setReorder(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <Field label={t('notes')}>
          {({ id }) => <Input id={id} dir="auto" value={notes} onChange={(e) => setNotes(e.target.value)} />}
        </Field>
        {editing && product && (
          <>
            <Button
              variant={product.active ? 'danger' : 'default'}
              block
              busy={toggle.isPending}
              onClick={() => toggle.mutate()}
            >
              {product.active ? t('deactivate') : t('activate')}
            </Button>
            {!product.has_history &&
              (confirmDelete ? (
                <>
                  <Notice tone="warning">{t('delete.confirm')}</Notice>
                  <Button variant="danger" block busy={remove.isPending} onClick={() => remove.mutate()}>
                    {t('delete')}
                  </Button>
                </>
              ) : (
                <Button variant="quiet" block onClick={() => setConfirmDelete(true)}>
                  {t('delete')}
                </Button>
              ))}
          </>
        )}
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('saveproduct')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-CAT-04: change one unit's price with a reason (owner). */
export function PriceSheet({
  product,
  price,
  onClose,
  onSaved,
}: {
  product: Product;
  price: ProductPrice | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refusal = useRefusalText();
  const money = useMoney();
  const allowed: Unit[] = [
    ...(product.sell_piece ? ['piece' as const] : []),
    ...(product.sell_box ? ['box' as const] : []),
  ];
  const [unit, setUnit] = useState<Unit>(allowed[0] ?? 'piece');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<{ field: 'price' | 'reason' | 'form'; text: string }>();
  const current = unit === 'piece' ? price?.piece_price : price?.box_price;
  const reasons = ['reason.supplier', 'reason.promo', 'reason.market'];
  const save = useMutation({
    mutationFn: () => changePrice(product.id, unit, parseAmount(value) ?? 0, reason.trim()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CATALOG });
      onSaved();
    },
    onError: (e) => {
      const code = refusalCode(e);
      setError({
        field:
          code === 'REASON_REQUIRED' ? 'reason' : code === 'INVALID_PRICE' || code === 'SAME_PRICE' ? 'price' : 'form',
        text: refusal(e),
      });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const p = parseAmount(value);
    if (p === null || p <= 0) return setError({ field: 'price', text: t('err.price') });
    if (p === current) return setError({ field: 'price', text: t('err.sameprice') });
    if (!reason.trim()) return setError({ field: 'reason', text: t('err.reason') });
    setError(undefined);
    save.mutate();
  };
  return (
    <BottomSheet
      title={t('changeprice')}
      sub={<Name>{product.name}</Name>}
      open
      onClose={onClose}
      busy={save.isPending}
    >
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {error?.field === 'form' && <Notice tone="danger">{error.text}</Notice>}
        {allowed.length > 1 && (
          <Field label={t('units')}>
            {() => (
              <Tabs
                label={t('units')}
                value={unit}
                onChange={(id) => setUnit(id as Unit)}
                items={allowed.map((u) => ({ id: u, label: t(`unit.${u}`) }))}
              />
            )}
          </Field>
        )}
        <FormGrid>
          <Field label={t('current')}>{({ id }) => <Input id={id} readOnly value={money(current)} />}</Field>
          <Field label={t('newprice')} error={error?.field === 'price' ? error.text : undefined}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                numeric
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid}
              />
            )}
          </Field>
        </FormGrid>
        <Field label={t('reason')} error={error?.field === 'reason' ? error.text : undefined}>
          {({ id, describedBy, invalid }) => (
            <>
              <Input
                id={id}
                dir="auto"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid}
              />
              <Chips
                label={t('reason')}
                items={reasons.map((r) => ({ id: r, label: t(r) }))}
                selected={reasons.filter((r) => t(r) === reason)}
                onToggle={(id) => setReason(t(id))}
              />
            </>
          )}
        </Field>
        <Notice tone="info">{t('price.applies')}</Notice>
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('changeprice')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-CAT-05: add a barcode to a unit (owner, Storekeeper). */
export function AddBarcodeSheet({ product, onClose }: { product: Product; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refusal = useRefusalText();
  const allowed: Unit[] = [
    ...(product.sell_piece ? ['piece' as const] : []),
    ...(product.sell_box ? ['box' as const] : []),
  ];
  const [unit, setUnit] = useState<Unit>(allowed[0] ?? 'piece');
  const [code, setCode] = useState('');
  const [source, setSource] = useState<'supplier' | 'internal'>('supplier');
  const [error, setError] = useState<string>();
  const [scanning, setScanning] = useState(false);
  const save = useMutation({
    mutationFn: () => addBarcode(product.id, unit, code.replace(/\s/g, ''), source),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CATALOG });
      onClose();
    },
    onError: (e) => setError(refusal(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{8}$|^\d{12,14}$/.test(code.replace(/\s/g, ''))) return setError(t('err.ean'));
    setError(undefined);
    save.mutate();
  };
  return (
    <BottomSheet title={t('addbarcode')} sub={<Name>{product.name}</Name>} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        <Field label={t('units')}>
          {() => (
            <Tabs
              label={t('units')}
              value={unit}
              onChange={(id) => setUnit(id as Unit)}
              items={allowed.map((u) => ({ id: u, label: t(`unit.${u}`) }))}
            />
          )}
        </Field>
        {scanning && (
          <CameraScan
            onCode={(read) => {
              setCode(read);
              setScanning(false);
            }}
          />
        )}
        <Field label={t('code')} error={error}>
          {({ id, describedBy, invalid }) => (
            <div className="yv-input-row">
              <Input
                id={id}
                numeric
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid}
              />
              <Button
                icon="scan"
                aria-label={t('scan')}
                aria-pressed={scanning}
                onClick={() => setScanning((v) => !v)}
              />
            </div>
          )}
        </Field>
        <Field label={t('source')}>
          {() => (
            <Chips
              label={t('source')}
              items={[
                { id: 'supplier', label: t('src.supplier') },
                { id: 'internal', label: t('src.internal') },
              ]}
              selected={[source]}
              onToggle={(id) => setSource(id as 'supplier' | 'internal')}
            />
          )}
        </Field>
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('addbarcode')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-CAT-05: generate an internal EAN-13 for a unit without a barcode (owner, Storekeeper). */
export function GenerateBarcodeSheet({
  product,
  units,
  onClose,
}: {
  product: Product;
  units: Unit[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refusal = useRefusalText();
  const [unit, setUnit] = useState<Unit>(units[0] ?? 'piece');
  const [error, setError] = useState<string>();
  const [made, setMade] = useState<string>();
  const save = useMutation({
    mutationFn: () => generateBarcode(product.id, unit),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: CATALOG });
      setMade(r.code);
    },
    onError: (e) => setError(refusal(e)),
  });
  return (
    <BottomSheet title={t('generate')} sub={<Name>{product.name}</Name>} open onClose={onClose} busy={save.isPending}>
      {error && <Notice tone="danger">{error}</Notice>}
      {units.length === 0 ? (
        <Notice tone="info">{t('err.genexists')}</Notice>
      ) : made ? (
        <Notice tone="info">
          {t(`unit.${unit}`)} · <Code>{made}</Code>
        </Notice>
      ) : (
        <>
          {units.length > 1 && (
            <Field label={t('units')}>
              {() => (
                <Tabs
                  label={t('units')}
                  value={unit}
                  onChange={(id) => setUnit(id as Unit)}
                  items={units.map((u) => ({ id: u, label: t(`unit.${u}`) }))}
                />
              )}
            </Field>
          )}
          <Notice tone="info">{t('src.internal')} · EAN-13 · 20…</Notice>
        </>
      )}
      <SheetActions>
        {!made && units.length > 0 && (
          <Button variant="main" busy={save.isPending} onClick={() => save.mutate()}>
            {t('gen.save')}
          </Button>
        )}
        <Button onClick={onClose}>{made ? t('done') : t('cancel')}</Button>
      </SheetActions>
    </BottomSheet>
  );
}

/** SCR-CAT-05: find a product by barcode (scan or type). A found code opens the product card on its unit. */
export function FindBarcodeSheet({
  onClose,
  onFound,
  camera = true,
}: {
  onClose: () => void;
  onFound: (productId: string) => void;
  /** False shows the empty viewfinder (tests, or devices without a camera). */
  camera?: boolean;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const find = async (value: string) => {
    setBusy(true);
    setMissing(false);
    try {
      const hit = await findBarcode(value.replace(/\s/g, ''));
      if (hit) onFound(hit.product_id);
      else setMissing(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <BottomSheet title={t('scan.title')} open onClose={onClose} busy={busy}>
      {camera ? (
        <CameraScan
          onCode={(read) => {
            setCode(read);
            void find(read);
          }}
        />
      ) : (
        <Scanner hint={t('scan.hint')} />
      )}
      {missing && <Notice tone="warning">{t('scan.unknown')}</Notice>}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (code.trim()) void find(code);
        }}
        noValidate
        className="yv-skeleton-stack"
      >
        <Field label={t('code')}>
          {({ id }) => (
            <Input
              id={id}
              numeric
              inputMode="numeric"
              placeholder={t('scan.type')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          )}
        </Field>
        <SheetActions>
          <Button type="submit" variant="main" busy={busy}>
            {t('find')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-CAT-06: new or renamed category (owner). */
export function CategorySheet({
  category,
  categories,
  onClose,
}: {
  category: Category | null;
  categories: Category[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refusal = useRefusalText();
  const [name, setName] = useState(category?.name ?? '');
  const [parent, setParent] = useState(category?.parent_id ?? '');
  const [error, setError] = useState<string>();
  const save = useMutation({
    mutationFn: () => saveCategory(category?.id ?? null, name.trim(), parent || null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CATALOG });
      onClose();
    },
    onError: (e) => setError(refusal(e, 'err.catdup')),
  });
  return (
    <BottomSheet title={t(category ? 'rename' : 'newcategory')} open onClose={onClose} busy={save.isPending}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return setError(t('err.required'));
          save.mutate();
        }}
        noValidate
        className="yv-skeleton-stack"
      >
        <Field label={t('name')} error={error}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="auto"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <Field label={t('parent')}>
          {({ id }) => (
            <Select id={id} value={parent} onChange={(e) => setParent(e.target.value)}>
              <option value="">{t('parent.none')}</option>
              {categories
                .filter((c) => c.id !== category?.id && c.depth < 3)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.path}
                  </option>
                ))}
            </Select>
          )}
        </Field>
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-CAT-06: new or renamed brand (owner). */
export function BrandSheet({ brand, onClose }: { brand: Brand | null; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refusal = useRefusalText();
  const [name, setName] = useState(brand?.name ?? '');
  const [error, setError] = useState<string>();
  const save = useMutation({
    mutationFn: () => saveBrand(brand?.id ?? null, name.trim()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CATALOG });
      onClose();
    },
    onError: (e) => setError(refusal(e, 'err.brand')),
  });
  return (
    <BottomSheet title={t(brand ? 'rename' : 'newbrand')} open onClose={onClose} busy={save.isPending}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return setError(t('err.required'));
          save.mutate();
        }}
        noValidate
        className="yv-skeleton-stack"
      >
        <Field label={t('name')} error={error}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="auto"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-CAT-10: the catalogue as CSV (owner). UTF-8 with BOM so Excel reads Arabic; `;` in French. */
export function ExportSheet({
  products,
  prices,
  costs,
  barcodes,
  onClose,
}: {
  products: Product[];
  prices: Map<string, ProductPrice>;
  costs: Map<string, ProductCost>;
  barcodes: Map<string, string[]>;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [scope, setScope] = useState<'active' | 'all'>('active');
  const [withCost, setWithCost] = useState(false);
  const [done, setDone] = useState<number>();
  const rows = products.filter((p) => scope === 'all' || p.active);
  const download = () => {
    const sep = i18n.language === 'fr' ? ';' : ',';
    const cell = (v: unknown) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const head = [
      t('name'),
      t('sku'),
      t('category'),
      t('brand'),
      t('capacity'),
      t('units'),
      t('pieceprice'),
      t('boxprice'),
      t('reorder'),
      t('status'),
      t('tab.barcodes'),
      ...(withCost ? [t('avgcost')] : []),
    ];
    const lines = rows.map((p) => [
      p.name,
      p.sku,
      p.category_path,
      p.brand,
      p.box_capacity,
      [p.sell_piece && t('unit.piece'), p.sell_box && t('unit.box')].filter(Boolean).join(' + '),
      prices.get(p.id)?.piece_price,
      prices.get(p.id)?.box_price,
      p.reorder_level,
      p.active ? t('active') : t('inactive'),
      (barcodes.get(p.id) ?? []).join(' '),
      ...(withCost ? [costs.get(p.id)?.avg_cost] : []),
    ]);
    const csv = '﻿' + [head, ...lines].map((l) => l.map(cell).join(sep)).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `yakvender-catalogue-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setDone(rows.length);
  };
  return (
    <BottomSheet title={t('exp.title')} open onClose={onClose}>
      {done !== undefined && <Notice tone="info">{t('exp.done', { n: done })}</Notice>}
      <Field label={t('exp.scope')}>
        {() => (
          <Chips
            label={t('exp.scope')}
            items={[
              { id: 'active', label: t('exp.active') },
              { id: 'all', label: t('exp.all') },
            ]}
            selected={[scope]}
            onToggle={(id) => setScope(id as 'active' | 'all')}
          />
        )}
      </Field>
      <Notice tone="info">{t('exp.cols')}</Notice>
      <Chips
        label={t('exp.cost')}
        items={[{ id: 'cost', label: t('exp.cost') }]}
        selected={withCost ? ['cost'] : []}
        onToggle={() => setWithCost((v) => !v)}
      />
      {rows.length === 0 && <Notice tone="warning">{t('export.empty')}</Notice>}
      <SheetActions>
        <Button variant="main" icon="download" disabled={rows.length === 0} onClick={download}>
          {t('exp.btn')}
        </Button>
        <Button onClick={onClose}>{done !== undefined ? t('done') : t('cancel')}</Button>
      </SheetActions>
    </BottomSheet>
  );
}
